import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react'
import styles from './CSMCalendar.module.css'
import AuthContext from '../../store/auth-context'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import { smPost } from './smApi'
import { prettifyOccasionType, emojiForIcon } from './smIcons'
import SMSetupState from './SMSetupState'

const SM_GET_CALENDAR_MONTH = 'sm_get_calendar_month'
const SM_GET_DAY_CELEBRATIONS = 'sm_get_day_celebrations'
const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']

const pad2 = (n) => String(n).padStart(2, '0')

const buildMonthGrid = (year, monthIndex) => {
    const firstDay = new Date(year, monthIndex, 1)
    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate()
    // JS getDay(): 0 = Sunday ... convert to Monday-first index
    const leadingBlanks = (firstDay.getDay() + 6) % 7
    const cells = []
    for (let i = 0; i < leadingBlanks; i++) cells.push(null)
    for (let day = 1; day <= daysInMonth; day++) cells.push(day)
    return cells
}

const todayRef = new Date()

const CSMCalendar = () => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [viewYear, setViewYear] = useState(todayRef.getFullYear())
    const [viewMonth, setViewMonth] = useState(todayRef.getMonth() + 1) // 1-12, matches payload
    const [selectedDay, setSelectedDay] = useState(todayRef.getDate())
    const [days, setDays] = useState({})
    const [hasOccasionData, setHasOccasionData] = useState(true)
    const [loading, setLoading] = useState(true)
    const [loadErrorMessage, setLoadErrorMessage] = useState(null)

    const [dayClients, setDayClients] = useState([])
    const [dayLoading, setDayLoading] = useState(false)
    const [dayErrorMessage, setDayErrorMessage] = useState(null)

    const loadMonth = useCallback(async () => {
        setLoading(true)
        setLoadErrorMessage(null)
        const res = await smPost(authCtx, activeStore, SM_GET_CALENDAR_MONTH, {
            year: viewYear,
            month: viewMonth,
        })
        if (res?.status === 'success' && res?.response) {
            setDays(res.response.days || {})
            setHasOccasionData(res.response.has_occasion_data !== false)
        } else {
            setLoadErrorMessage(res?.message || "Couldn't load the calendar right now.")
        }
        setLoading(false)
    }, [authCtx, activeStore, viewYear, viewMonth])

    useEffect(() => {
        if (activeStore?.storeIdentifier) loadMonth()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier, viewYear, viewMonth])

    const loadDayCelebrations = useCallback(async (day) => {
        setDayLoading(true)
        setDayErrorMessage(null)
        const dateStr = `${viewYear}-${pad2(viewMonth)}-${pad2(day)}`
        const res = await smPost(authCtx, activeStore, SM_GET_DAY_CELEBRATIONS, { date: dateStr })
        if (res?.status === 'success' && res?.response) {
            setDayClients(res.response.clients || [])
        } else {
            setDayErrorMessage(res?.message || "Couldn't load this day's celebrations.")
            setDayClients([])
        }
        setDayLoading(false)
    }, [authCtx, activeStore, viewYear, viewMonth])

    useEffect(() => {
        if (!selectedDay) {
            setDayClients([])
            return
        }
        if (activeStore?.storeIdentifier) loadDayCelebrations(selectedDay)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier, selectedDay, viewYear, viewMonth])

    const cells = useMemo(() => buildMonthGrid(viewYear, viewMonth - 1), [viewYear, viewMonth])
    const monthLabel = useMemo(
        () => new Date(viewYear, viewMonth - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }),
        [viewYear, viewMonth]
    )

    const goToMonth = (direction) => {
        let nextMonth = viewMonth + direction
        let nextYear = viewYear
        if (nextMonth < 1) { nextMonth = 12; nextYear -= 1 }
        if (nextMonth > 12) { nextMonth = 1; nextYear += 1 }
        setSelectedDay(null)
        setViewYear(nextYear)
        setViewMonth(nextMonth)
    }

    if (loading) {
        return (
            <div className={styles.loadingWrap}>
                <RefreshCw size={18} className={styles.spinner} />
                <span>Loading calendar…</span>
            </div>
        )
    }

    if (loadErrorMessage) {
        return (
            <div className={styles.loadingWrap}>
                <span>{loadErrorMessage}</span>
                <button type="button" className={styles.retryBtn} onClick={loadMonth}>Retry</button>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            <div className={styles.pageHeader}>
                <h1 className={styles.pageTitle}>Calendar</h1>
                <p className={styles.pageSubtitle}>Think in dates — useful for planning campaigns ahead of time.</p>
            </div>

            <div className={styles.monthBar}>
                <button type="button" className={styles.monthNavBtn} onClick={() => goToMonth(-1)} aria-label="Previous month">
                    <ChevronLeft size={16} />
                </button>
                <span className={styles.monthLabel}>{monthLabel}</span>
                <button type="button" className={styles.monthNavBtn} onClick={() => goToMonth(1)} aria-label="Next month">
                    <ChevronRight size={16} />
                </button>
            </div>

            {!hasOccasionData ? (
                <SMSetupState />
            ) : (
                <>
                    <div className={styles.grid}>
                        {WEEKDAYS.map((wd) => (
                            <div key={wd} className={styles.weekdayCell}>{wd}</div>
                        ))}
                        {cells.map((day, idx) => {
                            if (day === null) return <div key={`blank-${idx}`} className={styles.emptyCell} />
                            const counts = days[String(day)]
                            const isSelected = day === selectedDay
                            return (
                                <button
                                    key={day}
                                    type="button"
                                    className={`${styles.dayCell} ${isSelected ? styles.dayCellSelected : ''}`}
                                    onClick={() => setSelectedDay(day)}
                                >
                                    <span className={styles.dayNumber}>{day}</span>
                                    {counts && (
                                        <span className={styles.dayBadges}>
                                            {counts.birthday && <span className={styles.badgeBirthday}>🎂{counts.birthday}</span>}
                                            {counts.anniversary && <span className={styles.badgeAnniversary}>💍{counts.anniversary}</span>}
                                        </span>
                                    )}
                                </button>
                            )
                        })}
                    </div>

                    <div className={styles.detailPanel}>
                        <div className={styles.detailHeader}>
                            {selectedDay ? `${selectedDay} ${monthLabel}` : 'Select a day'}
                        </div>

                        {dayLoading && (
                            <div className={styles.detailEmpty}>
                                <RefreshCw size={13} className={styles.spinner} /> Loading…
                            </div>
                        )}

                        {!dayLoading && dayErrorMessage && (
                            <div className={styles.detailEmpty}>
                                {dayErrorMessage}{' '}
                                <button type="button" className={styles.retryBtn} onClick={() => loadDayCelebrations(selectedDay)}>Retry</button>
                            </div>
                        )}

                        {!dayLoading && !dayErrorMessage && selectedDay && dayClients.length === 0 && (
                            <div className={styles.detailEmpty}>No celebrations on this day.</div>
                        )}

                        {!dayLoading && !dayErrorMessage && dayClients.length > 0 && (
                            <div className={styles.detailList}>
                                {dayClients.map((c) => (
                                    <Link
                                        key={c.occasion_id}
                                        to={`/special-moments/customer/${c.client_store_relation_identifier}`}
                                        className={styles.detailRow}
                                    >
                                        <span className={styles.detailEmoji}>{emojiForIcon(c.icon)}</span>
                                        <span className={styles.detailName}>{c.name}</span>
                                        <span className={styles.detailOccasion}>{prettifyOccasionType(c.occasion_type)}</span>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    )
}

export default CSMCalendar
